import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Users, 
  Search, 
  Eye,
  Globe,
  Calendar,
  Mail,
  Phone,
  FileText,
  ExternalLink,
  Briefcase,
  XCircle,
  Clock,
  Star,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import SendAppointmentModal from '../../components/appointments/SendAppointmentModal';
import AppointmentStatusIndicator from '../../components/appointments/AppointmentStatusIndicator';
import CVPreview from '../../components/CVPreview';
import ApplicationModal from '../../components/applications/ApplicationModal';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { applicationsAPI, handleApiError } from '../../utils/api';
import { cvsAPI } from '../../utils/api';

const ApplicationsPage = () => {
  const { user } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [showCVPreviewModal, setShowCVPreviewModal] = useState(false);
  const [cvPreviewData, setCvPreviewData] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedCandidateForAppointment, setSelectedCandidateForAppointment] = useState(null);
  const [deleteModalState, setDeleteModalState] = useState({
    show: false,
    application: null,
    option: 'keepCv',
    hasActiveAppointments: false
  });

  const closeDeleteModal = () => {
    setDeleteModalState({
      show: false,
      application: null,
      option: 'keepCv',
      hasActiveAppointments: false
    });
  };
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Helper function to get locale for date formatting
  const getDateLocale = () => {
    switch (currentLanguage) {
      case 'fr': return 'fr-FR';
      case 'es': return 'es-ES';
      case 'en': 
      default: return 'en-US';
    }
  };

  const statsQuery = useQuery(
    ['applicationsStats'],
    async () => {
      const response = await applicationsAPI.getStatsByOffer();
      return response.data;
    },
    {
      onError: (error) => {
        toast.error(handleApiError(error, t('dashboard.applications.messages.dataLoadError')));
      }
    }
  );

  const applicationsQuery = useQuery(
    ['applications', { selectedOffer, statusFilter, searchTerm: debouncedSearchTerm, currentPage }],
    async () => {
      const response = await applicationsAPI.getAll({
        offer_id: selectedOffer,
        status: statusFilter !== 'all' ? statusFilter : '',
        search: debouncedSearchTerm,
        page: currentPage,
        limit: itemsPerPage
      });
      return response.data;
    },
    {
      enabled: Boolean(selectedOffer),
      keepPreviousData: true,
      onError: (error) => {
        toast.error(handleApiError(error, t('dashboard.applications.messages.dataLoadError')));
      }
    }
  );

  const publishedOffers = useMemo(
    () => (statsQuery.data?.stats || []).map((offer) => ({ ...offer, offer_id: String(offer.offer_id) })),
    [statsQuery.data?.stats]
  );
  const applications = applicationsQuery.data?.applications || [];
  const totalApplications = applicationsQuery.data?.total || 0;
  const totalPages = applicationsQuery.data?.totalPages || 1;
  const loading = statsQuery.isLoading || (selectedOffer ? applicationsQuery.isLoading : false);

  const selectedOfferStats = useMemo(() => {
    if (!selectedOffer) return null;
    return publishedOffers.find((offer) => offer.offer_id === String(selectedOffer)) || null;
  }, [publishedOffers, selectedOffer]);

  const summaryStats = useMemo(() => {
    return publishedOffers.reduce(
      (acc, offer) => {
        acc.totalOffers += 1;
        if (offer.is_published) acc.publishedOffers += 1;
        acc.totalApplications += Number(offer.total_applications || 0);
        acc.newApplications += Number(offer.new_applications || 0);
        acc.shortlisted += Number(offer.shortlisted_applications || 0);
        return acc;
      },
      { totalOffers: 0, publishedOffers: 0, totalApplications: 0, newApplications: 0, shortlisted: 0 }
    );
  }, [publishedOffers]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage((prev) => (prev === 1 ? prev : 1));
  }, [selectedOffer, statusFilter, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!selectedOffer && publishedOffers.length > 0) {
      setSelectedOffer(publishedOffers[0].offer_id);
    }
  }, [publishedOffers, selectedOffer]);

  // View application details
  const viewApplication = async (applicationId) => {
    try {
      const response = await applicationsAPI.getById(applicationId);
      setSelectedApplication(response.data.application);
      setShowApplicationModal(true);
      // Notes will be loaded by the modal itself
    } catch (error) {
      toast.error(t('dashboard.applications.messages.applicationLoadError'));
    }
  };

  // Changer le statut d'une candidature
  const updateApplicationStatus = async (applicationId, newStatus, notes = '') => {
    try {
      await applicationsAPI.updateStatus(applicationId, { status: newStatus, notes });
      toast.success(t('dashboard.applications.messages.statusUpdated'));
      queryClient.invalidateQueries('applications');
      queryClient.invalidateQueries('applicationsStats');
      if (selectedApplication && selectedApplication.id === applicationId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus, notes });
      }
    } catch (error) {
      toast.error(handleApiError(error, t('dashboard.applications.messages.statusUpdateError')));
    }
  };

  // Supprimer une candidature
  const handleDeleteApplication = async (applicationId, candidateName) => {
    // Reset state with initial values
    setDeleteModalState({
      show: false,
      application: { id: applicationId, name: candidateName },
      option: 'keepCv',
      hasActiveAppointments: false
    });
    
    // Vérifier d'abord s'il y a des rendez-vous actifs
    try {
      const response = await applicationsAPI.checkAppointments(applicationId);
      const data = response.data;
      
      // Mettre à jour l'état avec toutes les données
      setDeleteModalState({
        show: true,
        application: { id: applicationId, name: candidateName },
        option: 'keepCv',
        hasActiveAppointments: data.hasActiveAppointments
      });
    } catch (error) {
      console.error('Error checking appointments:', error);
      setDeleteModalState({
        show: true,
        application: { id: applicationId, name: candidateName },
        option: 'keepCv',
        hasActiveAppointments: false
      });
    }
  };

  // Confirmer la suppression
  const confirmDeleteApplication = async () => {
    if (!deleteModalState.application) return;
    
    try {
      await applicationsAPI.delete(deleteModalState.application.id, { keepCv: deleteModalState.option === 'keepCv' });
      if (deleteModalState.option === 'keepCv') {
        toast.success(t('dashboard.applications.messages.applicationDeletedCvKept'));
      } else {
        toast.success(t('dashboard.applications.messages.applicationDeleted'));
      }
      
      queryClient.invalidateQueries('applications');
      queryClient.invalidateQueries('applicationsStats');
      if (selectedApplication && selectedApplication.id === deleteModalState.application.id) {
        setShowApplicationModal(false);
        setSelectedApplication(null);
      }
      
      closeDeleteModal();
    } catch (error) {
      toast.error(handleApiError(error, t('dashboard.applications.messages.deleteError')));
    }
  };

  // Navigate to the CV associated with the application
  const viewAssociatedCV = (application) => {
    if (application.cv_id) {
      // Redirect to CVs page with cv_id parameter to highlight it
      navigate(`/dashboard/cvs?highlight=${application.cv_id}`);
      setShowApplicationModal(false);
      setSelectedApplication(null);
    } else {
      toast.error(t('dashboard.applications.messages.noCVAssociated'));
    }
  };

  const fetchCvData = async (application) => {
    if (!application.cv_id) {
      toast.error(t('dashboard.applications.messages.noCVAssociated'));
      return null;
    }

    try {
      const response = await cvsAPI.getById(application.cv_id);
      return response.data;
    } catch (error) {
      toast.error(t('dashboard.applications.messages.cvLoadError'));
      return null;
    }
  };

  // Preview CV in modal
  const previewCV = async (application) => {
    const cvData = await fetchCvData(application);
    if (cvData) {
      setCvPreviewData(cvData);
      setShowCVPreviewModal(true);
    }
  };

  // Copier l'URL publique
  const copyPublicUrl = (offer) => {
    const fullUrl = `${window.location.origin}/jobs/${offer.public_url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success(t('dashboard.applications.publishedOffers.urlCopied'));
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Pagination Component
  const PaginationComponent = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(totalPages);
        }
      }
      
      return pages;
    };

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <div className="flex items-center text-sm text-gray-700">
          <span>
            {t('dashboard.applications.pagination.showing', {
              start: ((currentPage - 1) * itemsPerPage) + 1,
              end: Math.min(currentPage * itemsPerPage, totalApplications),
              total: totalApplications
            })}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('common.previous')}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-3 py-1 text-gray-400">...</span>
              ) : (
                <button
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-1 text-sm rounded ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label={t('common.next')}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Composants de statut
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      received: { icon: Clock, color: 'bg-blue-100 text-blue-800', label: t('dashboard.applications.status.new') },
      reviewed: { icon: Eye, color: 'bg-yellow-100 text-yellow-800', label: t('dashboard.applications.status.reviewed') },
      shortlisted: { icon: Star, color: 'bg-green-100 text-green-800', label: t('dashboard.applications.status.shortlisted') },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-800', label: t('dashboard.applications.status.rejected') }
    };

    const config = statusConfig[status] || statusConfig.received;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{t('dashboard.applications.title')}</h1>
        <p className="text-sm text-gray-600 mt-1">{t('dashboard.applications.subtitle')}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t('dashboard.applications.publishedOffers.title')}</p>
            <Globe className="h-4 w-4 text-indigo-500" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{summaryStats.publishedOffers}</p>
          <p className="text-xs text-gray-500 mt-1">{t('dashboard.applications.selectOfferPrompt')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t('dashboard.applications.applicationsReceived')}</p>
            <Users className="h-4 w-4 text-blue-500" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{summaryStats.totalApplications}</p>
          <p className="text-xs text-gray-500 mt-1">{t('dashboard.applications.noApplicationsHint')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t('dashboard.applications.status.new')}</p>
            <Clock className="h-4 w-4 text-blue-500" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-blue-700">{summaryStats.newApplications}</p>
          <p className="text-xs text-gray-500 mt-1">{t('dashboard.applications.publishedOffers.newApplications')}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-gray-500">{t('dashboard.applications.status.shortlisted')}</p>
            <Star className="h-4 w-4 text-emerald-500" />
          </div>
          <p className="mt-2 text-2xl font-semibold text-emerald-700">{summaryStats.shortlisted}</p>
          <p className="text-xs text-gray-500 mt-1">{t('dashboard.applications.publishedOffers.shortlistedApplications')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Published offers list */}
        <div className="xl:col-span-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm h-full">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">{t('dashboard.applications.publishedOffers.title')}</h2>
            </div>
            <div className="px-4 py-5 sm:p-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : publishedOffers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Globe className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">{t('dashboard.applications.publishedOffers.noOffers')}</p>
                  <p className="text-xs mt-1">{t('dashboard.applications.publishedOffers.noOffersHint')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {publishedOffers.map((offer) => (
                    <div
                      key={offer.offer_id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedOffer === offer.offer_id
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedOffer(offer.offer_id)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 text-sm">
                            {offer.offer_title}
                          </h3>
                          {!offer.is_published && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 mt-1">
                              {t('dashboard.applications.publishedOffers.unpublished')}
                            </span>
                          )}
                        </div>
                        {offer.is_published && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyPublicUrl(offer);
                            }}
                            className="text-gray-400 hover:text-blue-600"
                            title={t('dashboard.applications.publishedOffers.copyUrl')}
                            aria-label={t('dashboard.applications.publishedOffers.copyUrl')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                        <div className="flex items-center">
                          <Users className="h-3 w-3 mr-1" />
                          {offer.total_applications} {offer.total_applications > 1 ? t('dashboard.applications.publishedOffers.applicationCountPlural') : t('dashboard.applications.publishedOffers.applicationCount')}
                        </div>
                        <div className="flex items-center">
                          <Eye className="h-3 w-3 mr-1" />
                          {offer.views_count} {offer.views_count > 1 ? t('dashboard.applications.publishedOffers.viewCountPlural') : t('dashboard.applications.publishedOffers.viewCount')}
                        </div>
                      </div>

                      {offer.total_applications > 0 && (
                        <div className="mt-3 flex space-x-1">
                          {offer.new_applications > 0 && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                              {offer.new_applications} {offer.new_applications > 1 ? t('dashboard.applications.publishedOffers.newApplicationsPlural') : t('dashboard.applications.publishedOffers.newApplications')}
                            </span>
                          )}
                          {offer.shortlisted_applications > 0 && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                              {offer.shortlisted_applications} {offer.shortlisted_applications > 1 ? t('dashboard.applications.publishedOffers.shortlistedApplicationsPlural') : t('dashboard.applications.publishedOffers.shortlistedApplications')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Liste des candidatures */}
        <div className="xl:col-span-8">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h2 className="text-lg font-medium text-gray-900">
                  {selectedOffer ? t('dashboard.applications.applicationsReceived') : t('dashboard.applications.selectOffer')}
                </h2>
                
                {selectedOffer && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Filtre de statut */}
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="h-9 text-sm border border-gray-300 rounded-lg px-3 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="all">{t('dashboard.applications.filters.allStatuses')}</option>
                      <option value="received">{t('dashboard.applications.filters.new')}</option>
                      <option value="reviewed">{t('dashboard.applications.filters.reviewed')}</option>
                      <option value="shortlisted">{t('dashboard.applications.filters.shortlisted')}</option>
                      <option value="rejected">{t('dashboard.applications.filters.rejected')}</option>
                    </select>

                    {/* Recherche */}
                    <div className="relative">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder={t('dashboard.applications.filters.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-9 pl-10 pr-4 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                )}
              </div>
              {selectedOfferStats && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                    {selectedOfferStats.offer_title}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                    {selectedOfferStats.total_applications || 0} {t('dashboard.applications.publishedOffers.applicationCountPlural')}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                    {selectedOfferStats.shortlisted_applications || 0} {t('dashboard.applications.status.shortlisted')}
                  </span>
                </div>
              )}
            </div>

            <div className="px-4 py-5 sm:p-6">
              {!selectedOffer ? (
                <div className="text-center py-12 text-gray-500">
                  <Briefcase className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">{t('dashboard.applications.selectOfferPrompt')}</p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : applications.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">{t('dashboard.applications.noApplicationsForOffer')}</p>
                  <p className="text-xs mt-1">{t('dashboard.applications.noApplicationsHint')}</p>
                </div>
              ) : (
                <div>
                  <div className="space-y-4">
                    {applications.map((application) => (
                      <motion.div
                        key={application.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="font-medium text-gray-900">
                                {application.first_name} {application.last_name}
                              </h3>
                              <StatusBadge status={application.status} />
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                              <div className="flex items-center">
                                <Mail className="h-4 w-4 mr-1" />
                                {application.candidate_email || application.email}
                              </div>
                              {(application.candidate_phone || application.phone) && (
                                <div className="flex items-center">
                                  <Phone className="h-4 w-4 mr-1" />
                                  {application.candidate_phone || application.phone}
                                </div>
                              )}
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                {new Date(application.created_at).toLocaleDateString(getDateLocale())}
                              </div>
                              {application.source && (
                                <div className="flex items-center">
                                  <Globe className="h-4 w-4 mr-1" />
                                  {application.source}
                                </div>
                              )}
                            </div>

                            {application.message && (
                              <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded mb-3 line-clamp-2">
                                {application.message}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-2 lg:ml-4 lg:justify-end">
                            <button
                              onClick={() => viewApplication(application.id)}
                              className="inline-flex items-center justify-center h-9 w-9 border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                              title={t('dashboard.applications.actions.viewDetails')}
                              aria-label={t('dashboard.applications.actions.viewDetails')}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={() => previewCV(application)}
                              className="inline-flex items-center justify-center h-9 w-9 border border-gray-200 text-gray-500 hover:text-green-600 hover:border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                              title={t('dashboard.applications.actions.previewCV')}
                              aria-label={t('dashboard.applications.actions.previewCV')}
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                            
                            <div className="flex items-center gap-2">
                              <AppointmentStatusIndicator
                                candidateEmail={application.candidate_email || application.email}
                                showLabel={false}
                                size="small"
                              />
                              <button
                                onClick={() => {
                                  setSelectedCandidateForAppointment({
                                    name: `${application.first_name} ${application.last_name}`,
                                    email: application.candidate_email || application.email,
                                    offerId: application.offer_id,
                                    position: application.offer_title || '',
                                    recruiterName: user?.name || ''
                                  });
                                  setShowAppointmentModal(true);
                                }}
                                className="inline-flex items-center justify-center h-9 w-9 border border-gray-200 text-gray-500 hover:text-primary-600 hover:border-primary-200 rounded-lg hover:bg-primary-50 transition-colors"
                                title={t('dashboard.applications.actions.sendAppointment')}
                                aria-label={t('dashboard.applications.actions.sendAppointment')}
                              >
                                <CalendarIcon className="h-4 w-4" />
                              </button>
                            </div>
                            
                            <button
                              onClick={() => handleDeleteApplication(application.id, `${application.first_name} ${application.last_name}`)}
                              className="inline-flex items-center justify-center h-9 w-9 border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                              title={t('dashboard.applications.actions.delete')}
                              aria-label={t('dashboard.applications.actions.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  <PaginationComponent />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Details modal */}
      {showApplicationModal && (
        <ApplicationModal
          application={selectedApplication}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedApplication(null);
          }}
          onStatusUpdate={updateApplicationStatus}
          onPreviewCV={() => fetchCvData(selectedApplication)}
          onManageCV={() => viewAssociatedCV(selectedApplication)}
        />
      )}

      {/* Send Appointment Modal */}
      <SendAppointmentModal
        isOpen={showAppointmentModal}
        onClose={() => {
          setShowAppointmentModal(false);
          setSelectedCandidateForAppointment(null);
        }}
        candidateData={selectedCandidateForAppointment}
      />

      {/* CV Preview modal */}
      <CVPreview
        cv={cvPreviewData}
        isOpen={showCVPreviewModal}
        onClose={() => {
          setShowCVPreviewModal(false);
          setCvPreviewData(null);
        }}
      />
      
      {/* Delete Application Modal */}
      <ConfirmationModal
        isOpen={deleteModalState.show && Boolean(deleteModalState.application)}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteApplication}
        type="danger"
        title={t('dashboard.applications.deleteApplicationModal.title')}
        message={t('dashboard.applications.deleteApplicationModal.message').replace('{{name}}', deleteModalState.application?.name || '')}
        confirmText={t('dashboard.applications.deleteApplicationModal.confirm')}
        cancelText={t('common.cancel')}
      >
        {deleteModalState.hasActiveAppointments && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              {t('dashboard.applications.deleteApplicationModal.activeAppointmentsNotice')}
            </p>
          </div>
        )}

        <div className="space-y-3 border border-gray-200 rounded-lg p-4">
          <label className="flex items-start cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
            <input
              type="radio"
              value="keepCv"
              checked={deleteModalState.option === 'keepCv'}
              onChange={(e) => setDeleteModalState((prev) => ({ ...prev, option: e.target.value }))}
              className="mt-1 mr-3 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {t('dashboard.applications.deleteApplicationModal.keepCvOption')}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {t('dashboard.applications.deleteApplicationModal.keepCvDescription')}
              </div>
            </div>
          </label>

          {!deleteModalState.hasActiveAppointments && (
            <label className={`flex items-start cursor-pointer p-3 rounded-lg transition-colors ${
              deleteModalState.option === 'deleteAll' ? 'bg-red-50 border border-red-200' : 'hover:bg-gray-50'
            }`}>
              <input
                type="radio"
                value="deleteAll"
                checked={deleteModalState.option === 'deleteAll'}
                onChange={(e) => setDeleteModalState((prev) => ({ ...prev, option: e.target.value }))}
                className="mt-1 mr-3 h-4 w-4 text-red-600 border-gray-300 focus:ring-red-500"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900">
                  {t('dashboard.applications.deleteApplicationModal.deleteAllOption')}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {t('dashboard.applications.deleteApplicationModal.deleteAllDescription')}
                </div>
              </div>
            </label>
          )}
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default ApplicationsPage;
